/**
 * React integration for the patient pending-sync queue (#1739).
 *
 * - useQueuedPatientUpdate(): drop-in replacement for useUpdatePatient in
 *   Medical History flows. Applies the answer locally, persists it to the
 *   durable queue, and resolves after durable acceptance — the patient
 *   continues without waiting on the network, while navigation can never
 *   outrun device ownership. Delivery happens FIFO with the write's stable
 *   Idempotency-Key (deployed by dashboard-api#1009; rollout tracked in #931).
 * - usePatientSyncStatus(): saving / saved / not-synced surface state.
 * - PatientSyncProvider: owns one processor for the active patient, hydrates
 *   after restart, resumes delivery when connectivity returns, and re-applies
 *   pending answers whenever the patient query refetches.
 *
 * The AsyncStorage payload here is a SYNC QUEUE over server state, not a
 * local data source: screens keep rendering from the patient query, with
 * pending writes merged on top until the server accepts them.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { onlineManager, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useApiClient, type ApiClient } from "@src/packages/api/louise-api";
import type { UpdatePatientInput } from "../hooks/usePatient";
import {
  buildUpdatePatientPatchOptions,
  prepareUpdatePatientInput,
  type UpdatePatientPayload,
} from "../update-patient-request";
import { mergePatientUpdate } from "./mergePatientUpdate";
import { PatientSyncManager } from "./patientSyncManager";
import {
  PatientSyncGate,
  type PatientSyncGateSnapshot,
} from "./patientSyncGate";
import { patientQueueStorageKey } from "./patientWriteQueue";
import { classifyPatientSyncOutcome } from "./patientSyncOutcome";

function createManager(
  queryClient: QueryClient,
  client: ApiClient,
  tenantId: string,
  patientId: string,
): PatientSyncManager {
  const queryKey = ["patient", tenantId, patientId];
  const storageKey = patientQueueStorageKey(tenantId, patientId);

  return new PatientSyncManager({
    loadRaw: () => AsyncStorage.getItem(storageKey),
    saveRaw: (raw) => AsyncStorage.setItem(storageKey, raw),
    sendWrite: async (write, signal) => {
      try {
        const result = await client.PATCH(
          "/{tenantId}/patients/{patientId}",
          buildUpdatePatientPatchOptions({
            tenantId,
            patientId,
            data: write.body as UpdatePatientPayload,
            idempotencyKey: write.id,
            signal,
          }),
        );
        const outcome = classifyPatientSyncOutcome(result);
        if (outcome.kind !== "delivered") return outcome;
        const response = result.data as { data?: unknown } | undefined;
        return { kind: "delivered", serverPatient: response?.data };
      } catch {
        return {
          kind: "transient",
          message: "Sync is temporarily unavailable.",
        };
      }
    },
    applyLocal: (body) => {
      queryClient.setQueryData(queryKey, (current: unknown) =>
        mergePatientUpdate(current, body),
      );
    },
    applyServer: (patient) => {
      queryClient.setQueryData(queryKey, patient);
    },
    onDrained: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
    now: () => new Date().toISOString(),
    randomId: () =>
      typeof globalThis.crypto?.randomUUID === "function"
        ? globalThis.crypto.randomUUID()
        : `${Math.floor(Date.now() / 0x10000)
            .toString(16)
            .padStart(8, "0")
            .slice(-8)}-${Math.floor(Date.now() % 0x10000)
            .toString(16)
            .padStart(4, "0")}-4${Math.floor(Math.random() * 0x1000)
            .toString(16)
            .padStart(3, "0")}-${(0x8000 + Math.floor(Math.random() * 0x4000))
            .toString(16)
            .padStart(4, "0")}-${Math.floor(Math.random() * 0x1000000000000)
            .toString(16)
            .padStart(12, "0")}`,
    setTimer: (fn, ms) => setTimeout(fn, ms),
    clearTimer: (handle) =>
      clearTimeout(handle as ReturnType<typeof setTimeout>),
  });
}

const PatientSyncContext = createContext<PatientSyncGate | undefined>(
  undefined,
);

function useSyncGate(): PatientSyncGate {
  const gate = useContext(PatientSyncContext);
  if (!gate) {
    throw new Error("PatientSyncProvider is missing");
  }
  return gate;
}

/**
 * Owns exactly one queue manager for the active tenant/patient identity.
 * Keeping it in context prevents independent Medical History hooks from
 * creating competing processors while allowing identity changes to dispose
 * stale transports, timers, and cache subscriptions.
 */
export function PatientSyncProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { client, tenantId, patientId } = useApiClient();
  const gateRef = useRef<PatientSyncGate | null>(null);
  if (!gateRef.current) gateRef.current = new PatientSyncGate();
  const gate = gateRef.current;
  const pendingDisposals = useRef(
    new Map<PatientSyncManager, ReturnType<typeof setTimeout>>(),
  );
  const gateDisposal = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (gateDisposal.current !== null) {
      clearTimeout(gateDisposal.current);
      gateDisposal.current = null;
    }
    return () => {
      // Same strict-effects probe pattern as manager disposal below: defer by
      // one task so a development setup → cleanup → setup cycle cancels it,
      // while a real unmount settles staged acceptances instead of leaving
      // awaiting screens hanging forever.
      gateDisposal.current = setTimeout(() => gate.dispose(), 0);
    };
  }, [gate]);

  const manager = useMemo(() => {
    if (!tenantId || !patientId) return null;
    return createManager(queryClient, client, tenantId, patientId);
  }, [queryClient, client, tenantId, patientId]);

  useEffect(() => {
    if (!manager || !tenantId || !patientId) return;
    const identity = patientQueueStorageKey(tenantId, patientId);
    gate.attach(manager, identity);
    const disposalMap = pendingDisposals.current;
    const pendingDisposal = disposalMap.get(manager);
    if (pendingDisposal !== undefined) {
      clearTimeout(pendingDisposal);
      disposalMap.delete(manager);
    }
    void manager.hydrate().catch(() => manager.kick());

    const unsubscribeOnline = onlineManager.subscribe((online) => {
      if (online) manager.kick();
    });

    const unsubscribeCache = queryClient.getQueryCache().subscribe((event) => {
      const eventKey = event.query.queryKey;
      if (
        event.type === "updated" &&
        event.action.type === "success" &&
        !("manual" in event.action && event.action.manual) &&
        eventKey.length === 3 &&
        eventKey[0] === "patient" &&
        eventKey[1] === tenantId &&
        eventKey[2] === patientId
      ) {
        manager.reapplyRetained();
      }
    });

    return () => {
      gate.detach(manager);
      unsubscribeOnline();
      unsubscribeCache();
      // React strict effects intentionally run setup → cleanup → setup once in
      // development. Defer disposal by one task so the same manager can cancel
      // it during that probe, while a real identity change still stops the old
      // patient's timers and transport.
      const handle = setTimeout(() => {
        manager.dispose();
        disposalMap.delete(manager);
      }, 0);
      disposalMap.set(manager, handle);
    };
  }, [gate, manager, patientId, queryClient, tenantId]);

  return (
    <PatientSyncContext.Provider value={gate}>
      {children}
    </PatientSyncContext.Provider>
  );
}

export type PatientSyncState = "checking" | "saved" | "saving" | "not-synced";

function usePatientSyncSnapshot(): PatientSyncGateSnapshot {
  const gate = useSyncGate();
  const subscribe = useCallback(
    (onStoreChange: () => void) => gate.subscribe(onStoreChange),
    [gate],
  );
  const getSnapshot = useCallback(() => gate.cachedSnapshot(), [gate]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Queue-backed replacement for useUpdatePatient in Medical History flows.
 * Resolves as soon as the answer is applied locally and durably persisted;
 * delivery and network retries happen in the background.
 */
export function useQueuedPatientUpdate() {
  const gate = useSyncGate();
  const snapshot = usePatientSyncSnapshot();

  const mutateAsync = useCallback(
    async (input: UpdatePatientInput) => {
      const { data } = prepareUpdatePatientInput(input);
      await gate.enqueue(data);
    },
    [gate],
  );

  return useMemo(
    () => ({ mutateAsync, isPending: snapshot.accepting > 0 }),
    [mutateAsync, snapshot.accepting],
  );
}

/**
 * Queue surface state: `saving` while a delivery is on the wire,
 * `not-synced` when answers are waiting (offline/backoff) or permanently
 * failed, `saved` when the queue is empty.
 */
export function usePatientSyncStatus(): PatientSyncGateSnapshot & {
  state: PatientSyncState;
  retryFailed: (id: string) => Promise<void>;
  beginEditFailed: (id: string) => boolean;
  editFailed: (id: string, input: UpdatePatientInput) => Promise<void>;
  dismissFailed: (id: string) => Promise<void>;
} {
  const gate = useSyncGate();
  const snapshot = usePatientSyncSnapshot();
  const retryFailed = useCallback(
    (id: string) => gate.retryFailedWrite(id),
    [gate],
  );
  const beginEditFailed = useCallback(
    (id: string) => gate.beginFailedWriteEdit(id),
    [gate],
  );
  const editFailed = useCallback(
    async (id: string, input: UpdatePatientInput) => {
      const { data } = prepareUpdatePatientInput(input);
      await gate.editFailedWrite(id, data);
    },
    [gate],
  );
  const dismissFailed = useCallback(
    (id: string) => gate.dismissFailedWrite(id),
    [gate],
  );

  const state: PatientSyncState =
    snapshot.storageError || snapshot.undurable > 0
      ? "not-synced"
      : !snapshot.hydrated
        ? "checking"
        : snapshot.inFlight
          ? "saving"
          : snapshot.pending > 0 || snapshot.failed.length > 0
            ? "not-synced"
            : "saved";

  return {
    ...snapshot,
    state,
    retryFailed,
    beginEditFailed,
    editFailed,
    dismissFailed,
  };
}
